import { NO_LIST_FILTERS, resolveAutoList, sameListFilters, type ListFilters } from './game-filters';
import { gameKey, type GameSourceRecord } from './game-sources';
import type { OwnListMode } from './owner-game-lists-api';
import { sameOrder, type SiteGame } from './site-games';

/**
 * The rules one of the owner's own lists follows in the Games tab, for both ways a list is built.
 *
 * A hand-picked list IS its order: the ids, in the owner's order, whether or not the catalogue still holds
 * them. An automatic list is its filters: every catalogue game they match, in the owner's order first and then
 * in catalogue order (`resolveAutoList`), so its order is only the pins over whatever the filters bring.
 */

/** One state of a list, the draft the owner is editing or what the site shows: how it is built, an automatic
 *  list's filters, and the order. */
export type OwnListState = { mode: OwnListMode; filters: ListFilters | null; order: readonly string[] };

/** The ids a list holds, in its order. */
export const ownListMembers = (state: OwnListState, catalogue: readonly GameSourceRecord[]): string[] =>
  state.mode === 'auto'
    ? resolveAutoList(catalogue, state.filters ?? NO_LIST_FILTERS, state.order).map(gameKey)
    : [...state.order];

/**
 * Whether the owner's working list differs from what the site shows. For an automatic list that is its filters
 * or the order its games come out in, compared as resolved: a game the platform adds joins both sides alike and
 * is no change of the owner's, while a reorder, a filter or a switch of mode is. A catalogue that could not be read
 * (`null`) resolves nothing on either side: the order is compared as stored then, so a failed read hides no change.
 */
export const ownListChanged = (
  working: OwnListState,
  published: OwnListState,
  catalogue: readonly GameSourceRecord[] | null,
): boolean => {
  if (working.mode !== published.mode) return true;
  if (working.mode === 'manual') return !sameOrder([...working.order], [...published.order]);
  if (!sameListFilters(working.filters ?? NO_LIST_FILTERS, published.filters ?? NO_LIST_FILTERS)) return true;
  if (catalogue === null) return !sameOrder([...working.order], [...published.order]);
  return !sameOrder(ownListMembers(working, catalogue), ownListMembers(published, catalogue));
};

/**
 * The games a list shows in the tab, in its order. In a hand-picked list an id the catalogue no longer holds is
 * a placeholder in its place (named in `unavailable`), so the owner sees it, can move it, and removes it on
 * purpose; nothing else knows it is there, and the site does not show it. An automatic list has none: a game
 * that leaves the catalogue simply stops matching.
 */
export const ownListGames = (
  state: OwnListState,
  catalogue: readonly GameSourceRecord[],
): { games: SiteGame[]; unavailable: ReadonlySet<string> } => {
  if (state.mode === 'auto') {
    return { games: resolveAutoList(catalogue, state.filters ?? NO_LIST_FILTERS, state.order), unavailable: new Set() };
  }
  const byId = new Map(catalogue.map((game) => [gameKey(game), game]));
  const unavailable = new Set<string>();
  const seen = new Set<string>();
  const games: SiteGame[] = [];
  state.order.forEach((id) => {
    if (id === '' || seen.has(id)) return;
    seen.add(id);
    const game = byId.get(id);
    if (!game) unavailable.add(id);
    games.push(game ?? { id });
  });
  return { games, unavailable };
};
